import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
  Label,
  InputText,
  Checkbox,
  Button,
  ErrorMessage,
  InputSelect,
} from '@teo/components';
import { axios } from '@/axios';
import { config } from '@/config';
import i18n from 'i18next';

const isValidUrl = (value) => {
  try {
    new URL(value);
    return true;
  } catch (_) {
    return false;
  }
};

const extractFileId = (url) => {
  try {
    const parsedUrl = new URL(url);

    if (parsedUrl.hostname === 'drive.google.com') {
      const regex = /\/d\/(.*?)\//;
      const matches = parsedUrl.pathname.match(regex);
      return matches ? matches[1] : null;
    } else {
      console.error('This URL does not belong to Google Drive.');
      return null;
    }
  } catch (error) {
    console.error('Invalid URL', error);
    return null;
  }
};

export const UploadByUrl = ({ setNewAsset, closeModal }) => {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [isAutoplay, setIsAutoplay] = useState(false);
  const [isLoop, setIsLoop] = useState(false);
  const [isAutoCC, setIsAutoCC] = useState(false);
  const [isUrl, setIsUrl] = useState(true);
  const [languageCC, setLanguageCC] = useState({
    value: 'en',
    label: `${i18n.t('langs.en')}`,
  });

  var languages = [
    { value: 'en', label: `${i18n.t('langs.en')}` },
    { value: 'nl', label: `${i18n.t('langs.nl')}` },
    { value: 'fr', label: `${i18n.t('langs.fr')}` },
    { value: 'de', label: `${i18n.t('langs.de')}` },
    { value: 'es', label: `${i18n.t('langs.es')}` },
    { value: 'it', label: `${i18n.t('langs.it')}` },
    { value: 'pt', label: `${i18n.t('langs.pt')}` },
    { value: 'bg', label: `${i18n.t('langs.bg')}` },
  ];

  const convertToEmbedUrl = (url) => {
    try {
      const parsed = new URL(url);

      // YouTube: youtu.be
      if (parsed.hostname === 'youtu.be') {
        const id = parsed.pathname.slice(1);
        return `https://www.youtube.com/embed/${id}`;
      }

      // YouTube: youtube.com
      if (
        parsed.hostname.includes('youtube.com') &&
        parsed.searchParams.get('v')
      ) {
        const id = parsed.searchParams.get('v');
        return `https://www.youtube.com/embed/${id}`;
      }

      // Vimeo
      if (parsed.hostname.includes('vimeo.com')) {
        const id = parsed.pathname.split('/').filter(Boolean)[0];
        return `https://player.vimeo.com/video/${id}`;
      }

      // Google Drive
      if (parsed.hostname.includes('drive.google.com')) {
        const fileId = extractFileId(url);
        return fileId
          ? `https://drive.google.com/file/d/${fileId}/preview`
          : null;
      }

      return url;
    } catch {
      return null;
    }
  };

  function extractYouTubeID(url) {
    const regex = /(?:youtube\.com\/.*v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  }

  const addSetings = (el) => {
    let newUrl = el;

    if (isLoop) {
      if (newUrl.includes('youtube.com') || newUrl.includes('youtu.be')) {
        if (newUrl.includes('?')) {
          newUrl += `&playlist=${extractYouTubeID(url)}&loop=1&`;
        } else {
          newUrl += `?playlist=${extractYouTubeID(url)}&loop=1`;
        }
      } else if (newUrl.includes('vimeo.com')) {
        if (newUrl.includes('?')) {
          newUrl += `&loop=1`;
        } else {
          newUrl += `?&loop=1`;
        }
      }
    }

    if (isAutoplay) {
      if (newUrl.includes('youtube.com') || newUrl.includes('youtu.be')) {
        if (newUrl.includes('?')) {
          newUrl += '&autoplay=1&mute=1&wmode=opaque&rel=0';
        } else {
          newUrl += '?&autoplay=1&mute=1&wmode=opaque&rel=0';
        }
      } else if (newUrl.includes('vimeo.com')) {
        if (newUrl.includes('?')) {
          newUrl += '&autoplay=1&muted=1&#t=0s';
        } else {
          newUrl += '?&autoplay=1&muted=1&#t=0s';
        }
      }
    }

    if (isAutoCC) {
      if (newUrl.includes('youtube.com') || newUrl.includes('youtu.be')) {
        if (languageCC?.value) {
          isAutoplay
            ? (newUrl += `&cc_load_policy=1&cc_lang_pref=${languageCC?.value}&`)
            : (newUrl += `?&cc_load_policy=1&cc_lang_pref=${languageCC?.value}`);
        } else {
          isAutoplay
            ? (newUrl += '&cc_load_policy=1&')
            : (newUrl += '?&cc_load_policy=1');
        }
      } else if (newUrl.includes('vimeo.com')) {
        if (languageCC?.value) {
          isAutoplay
            ? (newUrl += `&texttrack=${languageCC?.value}`)
            : (newUrl += `?&texttrack=${languageCC?.value}`);
        }
      }
    }

    return newUrl;
  };

  const handleUplod = async () => {
    const validUrl = isValidUrl(url);
    setIsUrl(validUrl);
    if (validUrl) {
      const embedUrl = convertToEmbedUrl(url);
      const finalUrl = addSetings(embedUrl);
      const dataVideo = await axios.post(`${config.backend}/assets`, {
        assetName: t('pages.create_edit_course.new_asset'),
        assetType: 'video',
        assetPath: finalUrl,
        // assetPath: embedUrl,
      });
      setNewAsset(dataVideo?.data);
      closeModal();
    }
  };

  return (
    <div className="flex flex-col">
      <div>
        <Label htmlFor="by-url">
          <p className="mt-6 text-sm font-semibold">
            {t('pages.create_edit_course.video_upload_modal.video_url')}
          </p>
        </Label>
        <InputText
          id="by-url"
          type="text"
          onChange={(e) => setUrl(e.currentTarget.value)}
          placeholder={'https://wwww.example...'}
          value={url}
          hasError={!isUrl}
        />
        {!isUrl && (
          <div className="mt-0 mb-2 text-sm text-tint-red-dark">
            <ErrorMessage id="errorMessage">
              {t('pages.create_edit_course.video_upload_modal.invalid_url')}
            </ErrorMessage>
          </div>
        )}
      </div>
      <div className="mt-4 flex min-h-[40px] items-center">
        <div className="mr-4 flex items-center border-r pr-4">
          <Checkbox
            id="isAutoplay"
            onChange={() => setIsAutoplay(!isAutoplay)}
            checked={isAutoplay}
          />

          <label htmlFor="isAutoplay" className="pl-2.5 text-sm text-black">
            {t('pages.create_edit_course.video_upload_modal.autoplay')}
          </label>
        </div>
        <div className="mr-4 flex items-center border-r pr-4">
          <Checkbox
            id="loop"
            onChange={() => setIsLoop(!isLoop)}
            checked={isLoop}
          />

          <label htmlFor="loop" className="pl-2.5 text-sm text-black">
            {t('pages.create_edit_course.video_upload_modal.loop')}
          </label>
        </div>
        <div className="mr-4 flex items-center">
          <Checkbox
            id="autocc"
            onChange={() => setIsAutoCC(!isAutoCC)}
            checked={isAutoCC}
          />

          <label htmlFor="autocc" className="pl-2.5 text-sm text-black">
            {t('pages.create_edit_course.video_upload_modal.auto_cc')}
          </label>
        </div>
        {isAutoCC && (
          <div className="w-[192px]">
            <InputSelect
              fullWidth
              style={{ padding: '0 500px' }}
              size="small"
              onChange={(lang) => {
                setLanguageCC(lang);
              }}
              value={languageCC}
              options={languages}
            >
              {(options) => <span>{options?.label}</span>}
            </InputSelect>
          </div>
        )}
      </div>
      <div className="mt-4 flex w-full">
        <Button className="ml-auto" onClick={() => handleUplod()}>
          {t('pages.create_edit_course.video_upload_modal.upload')}
        </Button>
      </div>
    </div>
  );
};
